import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  FlaskConical,
  Building2,
  Tag,
  CalendarDays,
  Users,
} from 'lucide-react';
import Header from './Header';
import { apiService } from '../services/api';
import { EmergingTrialDetail } from '../types';

function phasePillClasses(phase?: string | null) {
  const p = (phase || '').toLowerCase();
  if (p.includes('phase 3') || p.includes('phase3')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (p.includes('phase 2') || p.includes('phase2')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (p.includes('phase 1') || p.includes('phase1')) return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

function phaseShort(phase?: string | null) {
  if (!phase) return '—';
  return phase.replace(/PHASE\s*/gi, 'Phase ').replace(/\s+/g, ' ').trim() || phase;
}

function statusPillClasses(status?: string | null) {
  const s = (status || '').toLowerCase();
  if (s.includes('recruit') || s.includes('active') || s.includes('enrolling')) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (s.includes('complete')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (s.includes('terminat') || s.includes('withdraw') || s.includes('suspend')) {
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }
  if (s.includes('not yet')) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function asArray(value?: string | string[] | null): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean) as string[];
  return [value];
}

const EmergingTrialPage: React.FC = () => {
  const { trialId } = useParams<{ trialId: string }>();
  const navigate = useNavigate();
  const [trial, setTrial] = useState<EmergingTrialDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!trialId) return;
      setLoading(true);
      setError(null);
      const res = await apiService.fetchEmergingTrial(Number(trialId));
      if (cancelled) return;
      if (res.success && res.data) {
        setTrial(res.data);
      } else {
        setError(res.error || 'Failed to load trial');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [trialId]);

  const sources = useMemo(() => asArray(trial?.source_name), [trial?.source_name]);
  const sourceUrls = useMemo(() => asArray(trial?.source), [trial?.source]);
  const dataDates = useMemo(() => asArray(trial?.data_date), [trial?.data_date]);

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />

      <main className="w-full max-w-5xl mx-auto px-3 sm:px-4 lg:px-6 py-8 space-y-6">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/ongoing-trials'))}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {loading ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error || !trial ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
            <p className="text-rose-600 font-medium">{error || 'Trial not found.'}</p>
          </div>
        ) : (
          <>
            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 sm:p-8 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${phasePillClasses(trial.phases)}`}
                >
                  {phaseShort(trial.phases)}
                </span>
                {(trial.trial_status || trial.overall_status) && (
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusPillClasses(trial.trial_status || trial.overall_status)}`}
                  >
                    {trial.trial_status || trial.overall_status}
                  </span>
                )}
                {trial.nct_id && (
                  <a
                    href={`https://clinicaltrials.gov/study/${trial.nct_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-xs font-mono text-slate-600 hover:text-indigo-700 hover:bg-indigo-50"
                    title="Open on ClinicalTrials.gov"
                  >
                    {trial.nct_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {trial.enrollment_size && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <Users className="w-3 h-3" />
                    n={trial.enrollment_size}
                  </span>
                )}
              </div>

              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
                  {trial.full_title || trial.trial_name || '—'}
                </h1>
                {trial.full_title && trial.trial_name && trial.trial_name !== trial.full_title && (
                  <p className="text-sm text-slate-500 mt-1">{trial.trial_name}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-slate-600">
                {trial.assets.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <FlaskConical className="w-4 h-4 text-emerald-500" />
                    {trial.assets.map((a, i) => (
                      <React.Fragment key={a.id}>
                        {i > 0 && <span className="text-slate-300">+</span>}
                        <Link
                          to={`/emerging-assets/${a.id}`}
                          className="font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                        >
                          {a.asset_name}
                        </Link>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {trial.company && (
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <Link
                      to={`/emerging-companies/${trial.company.id}`}
                      className="text-slate-700 hover:text-indigo-700 hover:underline"
                    >
                      {trial.company.company_name}
                    </Link>
                    {trial.company.stock_ticker && (
                      <span className="text-xs font-mono text-slate-500">({trial.company.stock_ticker})</span>
                    )}
                  </div>
                )}
                {trial.indications.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Tag className="w-4 h-4 text-violet-500" />
                    {trial.indications.map((ind) => (
                      <Link
                        key={ind.code}
                        to={`/ongoing-trials?browse=indication&indication=${encodeURIComponent(ind.code)}`}
                        className="rounded-full bg-violet-50 text-violet-700 hover:bg-violet-100 hover:text-violet-900 px-2 py-0.5 text-xs font-medium"
                      >
                        {ind.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Key dates / metadata grid */}
            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 sm:p-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-4 flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                Timeline
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Study Start</dt>
                  <dd className="mt-1 text-slate-900">{formatDate(trial.study_start_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Primary Completion</dt>
                  <dd className="mt-1 text-slate-900">{formatDate(trial.primary_completion_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Study Completion</dt>
                  <dd className="mt-1 text-slate-900">{formatDate(trial.study_completion_date || trial.completion_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Last Updated</dt>
                  <dd className="mt-1 text-slate-900">
                    {formatDate(trial.last_updated_post_date || trial.last_update_submit_date)}
                  </dd>
                </div>
                {trial.status_verified_date && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Status Verified</dt>
                    <dd className="mt-1 text-slate-900">{formatDate(trial.status_verified_date)}</dd>
                  </div>
                )}
                {trial.sponsor && (
                  <div className="lg:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Sponsor</dt>
                    <dd className="mt-1 text-slate-900">{trial.sponsor}</dd>
                  </div>
                )}
                {trial.organization_full_name && trial.organization_full_name !== trial.sponsor && (
                  <div className="lg:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Organization</dt>
                    <dd className="mt-1 text-slate-900">{trial.organization_full_name}</dd>
                  </div>
                )}
              </dl>
            </section>

            {trial.brief_summary && (
              <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 sm:p-8">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">Brief Summary</h2>
                <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{trial.brief_summary}</p>
              </section>
            )}

            {(trial.intervention_names && trial.intervention_names.length > 0) && (
              <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 sm:p-8">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">Interventions</h2>
                <ul className="flex flex-wrap gap-2">
                  {trial.intervention_names.map((name, i) => (
                    <li
                      key={`${name}-${i}`}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Indication Details</h3>
                <p className="mt-1 text-slate-800 whitespace-pre-line">{trial.indication_details || '—'}</p>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Primary Endpoints</h3>
                <p className="mt-1 text-slate-800 whitespace-pre-line">{trial.primary_endpoints || '—'}</p>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Secondary Endpoints</h3>
                <p className="mt-1 text-slate-800 whitespace-pre-line">{trial.secondary_endpoints || '—'}</p>
              </div>
              {(trial.primary_outcome_measures && trial.primary_outcome_measures.length > 0) && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Primary Outcome Measures</h3>
                  <ul className="mt-1 list-disc list-inside text-slate-800 space-y-1">
                    {trial.primary_outcome_measures.map((measure, i) => (
                      <li key={i}>{measure}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {(sources.length > 0 || sourceUrls.length > 0) && (
              <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 sm:p-8">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">Sources</h2>
                <ul className="space-y-2 text-sm">
                  {(sources.length ? sources : sourceUrls).map((label, i) => {
                    const url = sourceUrls[i];
                    const display = label || url || '—';
                    return (
                      <li key={i} className="text-slate-700">
                        {url && /^https?:\/\//i.test(url) ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            {display}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span>{display}</span>
                        )}
                        {dataDates[i] && <span className="ml-2 text-xs text-slate-500">({dataDates[i]})</span>}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default EmergingTrialPage;
